import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { trackInterest } from "@/lib/trackInterest";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const saveBookmark = ({ analyticsKind, analyticsId, bookmarkTitle, bookmarkUrl }) => {
  if (!bookmarkTitle || !bookmarkUrl) return;
  try {
    // Non-sensitive: only which public preview was played, for the "keep reading" nudge on the next visit.
    localStorage.setItem("tacc_last_preview", JSON.stringify({
      kind: analyticsKind || "book", id: analyticsId || "", title: bookmarkTitle, url: bookmarkUrl, at: Date.now(),
    }));
  } catch (e) { console.warn("Bookmark save skipped:", e); }
};

// Fetches narration from POST /api/tts/generate on demand and drives one HTMLAudio element.
export const useTtsPlayer = ({ text, analyticsKind, analyticsId, bookmarkTitle, bookmarkUrl, autoPlay = false }) => {
  const [state, setState] = useState("idle"); // idle | loading | ready | playing | error
  const [enabled, setEnabled] = useState(null);
  const audioRef = useRef(null);
  const objectUrlRef = useRef(null);
  const trackedRef = useRef(false);
  const autoRef = useRef(false);

  useEffect(() => {
    let alive = true;
    axios.get(`${API}/tts/config`).then((r) => { if (alive) setEnabled(!!r.data?.enabled); }).catch(() => { if (alive) setEnabled(false); });
    return () => {
      alive = false;
      if (objectUrlRef.current) URL.revokeObjectURL(objectUrlRef.current);
    };
  }, []);

  const play = async () => {
    if (audioRef.current && state === "playing") { audioRef.current.pause(); return; }
    if (audioRef.current && state === "ready") { audioRef.current.play(); return; }
    setState("loading");
    if (!trackedRef.current && analyticsKind && analyticsId) {
      trackedRef.current = true;
      axios.post(`${API}/analytics/preview`, { kind: analyticsKind, id: analyticsId }).catch(() => {});
      trackInterest(analyticsKind, analyticsId, bookmarkTitle);
    }
    saveBookmark({ analyticsKind, analyticsId, bookmarkTitle, bookmarkUrl });
    try {
      const res = await axios.post(`${API}/tts/generate`, { text }, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      objectUrlRef.current = url;
      const a = new Audio(url);
      a.onplay = () => setState("playing");
      a.onpause = () => setState("ready");
      a.onended = () => setState("ready");
      a.onerror = () => setState("error");
      audioRef.current = a;
      await a.play();
    } catch {
      setState("error");
    }
  };
  const playRef = useRef(play);
  playRef.current = play;

  useEffect(() => {
    if (autoPlay && enabled && !autoRef.current) { autoRef.current = true; playRef.current(); }
  }, [autoPlay, enabled]);

  return { state, enabled, play };
};
